/**
 * EvieGroundLayer — the "page is the paper" ground, inside the applicant
 * boundary.
 *
 * A token-driven port of the Evie scene's ground layer: a fiber grain under a
 * soft diagonal light, one fixed host carrying both as ordered children (grain
 * first, light second — the paint order is the treatment). No MUI, no theme
 * object, no `alpha()`; the two sublayer effects live in `evie-shell.css` as
 * `.evie-ground-grain` / `.evie-ground-light` and take every
 * colour from an `--ds-*` variable through `color-mix`.
 *
 * THIS IS NOW THE ONLY GROUND. A second treatment used to exist —
 * `.evie-ground`, the radial/grid rule rendered by UiPage's
 * `showGround` — expressing the same INTENT with a different look. It was also
 * verified DEAD: its host was `absolute; z-index: -10` inside a `<main>` that
 * is `position: relative` with `z-index: auto`, which establishes no stacking
 * context, so it resolved against `.evie-shell` and was painted over by
 * `<main>`'s own opaque canvas. Confirmed in the browser — it never rendered,
 * on the preview page or anywhere else. Retiring it was therefore free: there
 * was no appearance to preserve, and both the host and the rule are gone. The
 * reconciliation is closed; this fiber ground is the approved treatment.
 *
 * STACKING CONTRACT — two requirements, and the second is the one that is easy
 * to miss:
 *
 *   1. An ancestor must establish a stacking context. `UiProvider`
 *      does (`.ui-scope { isolation: isolate }`), which is what lets this
 *      layer paint ABOVE the provider's canvas fill and BENEATH in-flow
 *      children. With no isolating ancestor a negative-z layer paints before
 *      `body`'s background box and vanishes behind the page colour.
 *   2. No opaque, positioned, `z-index: auto` box may sit between this layer
 *      and the content. Such a box paints at its context's positioned step —
 *      after the negative-z step — so it covers the ground even though it is
 *      "behind" the content. That is exactly what killed the retired page
 *      ground. `UiPage` therefore drops its canvas fill when
 *      `showGround` is false; a page rendered under a shell that mounts this
 *      layer must pass `showGround={false}` so it stays transparent over it.
 *
 * `isolation: isolate` creates no containing block for fixed descendants, so
 * the layer stays viewport-sized under the provider. An ancestor with a
 * `transform`, `filter`, `perspective`, or `contain` WOULD become that
 * containing block and clip the ground to it — don't wrap it in one.
 *
 * Decorative throughout: `aria-hidden`, no role, no content, pointer-events
 * none. Nothing animates, so there is no motion to gate on
 * `prefers-reduced-motion` — the boundary's global clamp has nothing to clamp
 * here.
 */

import './evie-ground.css'

interface EvieGroundLayerProps {
  /**
   * Composition only. The layer owns its position, stacking, and paint; a
   * caller has no business overriding those.
   */
  className?: string
}

/* Fixed to the viewport at the negative-z step of the nearest stacking
 * context — see the stacking contract above. */
const hostClasses = 'ds:pointer-events-none ds:fixed ds:inset-0 ds:-z-10'

const sublayerClasses = 'ds:absolute ds:inset-0'

export const EvieGroundLayer = ({ className }: EvieGroundLayerProps) => (
  <div
    aria-hidden="true"
    data-testid="evie-ground-layer"
    className={[hostClasses, className].filter(Boolean).join(' ')}
  >
    <div
      data-testid="evie-ground-layer-grain"
      className={`evie-ground-grain ${sublayerClasses}`}
    />
    <div
      data-testid="evie-ground-layer-light"
      className={`evie-ground-light ${sublayerClasses}`}
    />
  </div>
)

export type { EvieGroundLayerProps }
